/**
 * Reveal — full-screen match-reveal takeover (design system, FOUNDATION — v5
 * "PIXEL SWEETHEART"; S-12, DESIGN-BRIEF v5 §5 "matches trigger a cute
 * celebration").
 *
 * Two registers (~1.5-2s, always skippable — backdrop click closes):
 *  - kind="classic" → the joyful one: PIXEL CONFETTI (candy squares + tiny
 *    pixel hearts) rains down while TICKER cheers above the avatars, which
 *    ease together. Sweet, bouncy, a little smug.
 *  - kind="reject"  → the FUNNY one: Ticker appears heartbroken, trembles
 *    theatrically, SHATTERS into pixel pieces… then pops back with a wink.
 *    The avatars still meet with a bounce + a bubblegum spark. Dark comedy
 *    played cute — no strobe, no gore, no screen shake (v5 §5).
 *
 * Presentation only: the caller resolves © keys and supplies actions as
 * children (e.g. "Say something" / "Keep swiping"). Under
 * prefers-reduced-motion everything collapses to a plain fade — no confetti,
 * no shatter (Ticker goes straight to the wink; the joke survives as a
 * still) (PRD §10).
 *
 * API unchanged from v2/v3/v4; `avatars` remains additive and optional.
 * The title keeps the stable `.reveal__title` hook (e2e).
 */
import { useMemo, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Mascot } from "./mascot.js";
import { PixelHeart } from "./pixel.js";
import { springs, usePrefersReducedMotion } from "./motion.js";

/* pixel confetti — candy squares + a few tiny pixel hearts */
const CONFETTI_COLORS = [
  "var(--accent)",
  "var(--gold)",
  "var(--like)",
  "var(--sr)",
  "var(--reject)",
];
const CONFETTI_COUNT = 30;

function ConfettiBurst() {
  const pieces = useMemo(
    () =>
      Array.from({ length: CONFETTI_COUNT }, (_, i) => ({
        left: Math.random() * 100,
        delay: Math.random() * 0.5,
        duration: 1.7 + Math.random() * 1.4,
        rotate: (Math.random() - 0.5) * 540,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        scale: 0.6 + Math.random() * 0.9,
        heart: i % 6 === 0, // every sixth piece is a tiny pixel heart
      })),
    [],
  );
  return (
    <>
      {pieces.map((p, i) => (
        <motion.span
          key={i}
          className={`rvfx__confetti${p.heart ? " rvfx__confetti--heart" : ""}`}
          style={
            p.heart
              ? { left: `${p.left}%`, color: p.color, scale: p.scale }
              : { left: `${p.left}%`, background: p.color, scale: p.scale }
          }
          initial={{ y: -30, opacity: 1, rotate: 0 }}
          animate={{ y: "105vh", opacity: [1, 1, 0.7], rotate: p.rotate }}
          transition={{ duration: p.duration, delay: p.delay, ease: "easeIn" }}
          aria-hidden="true"
        >
          {p.heart && <PixelHeart size={12} />}
        </motion.span>
      ))}
    </>
  );
}

/* deterministic shard fan — Ticker's pixel pieces mid-flight */
const SHARD_COUNT = 12;
const SHARD_COLORS = [
  "var(--px-body, #ff6d95)",
  "var(--px-shade, #e0446f)",
  "var(--px-hi, #ffa9c1)",
  "var(--px-outline, #59243c)",
];

/**
 * The reject beat: heartbroken → tremble → shatter → wink. One shared
 * timeline (seconds): pop-in 0-0.25, tremble 0.35-0.75, burst at 0.8
 * (mascot blinks out, shards fly), wink pops back at 1.35 on a jelly spring.
 */
function TickerShatterBeat() {
  const shards = useMemo(
    () =>
      Array.from({ length: SHARD_COUNT }, (_, i) => {
        const angle = (i / SHARD_COUNT) * Math.PI * 2 + 0.35;
        const dist = 46 + (i % 3) * 22;
        return {
          x: Math.cos(angle) * dist,
          y: Math.sin(angle) * dist * 0.8 + 26, // a hint of gravity
          rotate: (i % 2 === 0 ? 1 : -1) * (120 + i * 24),
          color: SHARD_COLORS[i % SHARD_COLORS.length],
          size: i % 3 === 0 ? 9 : 6,
        };
      }),
    [],
  );
  return (
    <div className="rvfx__mascot" aria-hidden="true">
      {/* act 1 — heartbroken, trembling, then gone */}
      <motion.span
        style={{ display: "inline-flex" }}
        initial={{ opacity: 0, scale: 0.4 }}
        animate={{
          opacity: [0, 1, 1, 1, 0],
          scale: [0.4, 1.08, 1, 1, 0.9],
          rotate: [0, 0, -4, 4, 0],
          transition: { duration: 0.8, times: [0, 0.3, 0.55, 0.8, 1] },
        }}
      >
        <Mascot mood="heartbroken" size={56} />
      </motion.span>
      {/* act 2 — the pixel pieces */}
      {shards.map((s, i) => (
        <motion.span
          key={i}
          className="rvfx__shard"
          style={{ background: s.color, width: s.size, height: s.size }}
          initial={{ opacity: 0, x: 0, y: 0, rotate: 0, scale: 1 }}
          animate={{
            opacity: [0, 1, 1, 0],
            x: [0, s.x * 0.6, s.x],
            y: [0, s.y * 0.5, s.y],
            rotate: s.rotate,
            scale: [1, 1, 0.6],
            transition: { duration: 0.55, delay: 0.78, ease: "easeOut", times: [0, 0.2, 0.7, 1] },
          }}
        />
      ))}
      {/* act 3 — reassembled, winking. It's a bit. */}
      <motion.span
        style={{ position: "absolute", display: "inline-flex" }}
        initial={{ opacity: 0, scale: 0.3, y: 8 }}
        animate={{
          opacity: 1,
          scale: 1,
          y: 0,
          transition: { ...springs.jelly, delay: 1.35 },
        }}
      >
        <Mascot mood="wink" size={56} />
      </motion.span>
    </div>
  );
}

/** The avatar pair — eases together (classic) or meets with a bounce (reject). */
function AvatarPair({
  avatars,
  kind,
  reduced,
}: {
  avatars: [string, string];
  kind: "classic" | "reject";
  reduced: boolean;
}) {
  const bump = kind === "reject" && !reduced;
  return (
    <div className="rvfx__avatars" aria-hidden="true">
      <motion.img
        className="rvfx__avatar rvfx__avatar--a"
        src={avatars[0]}
        alt=""
        initial={reduced ? { opacity: 0 } : { opacity: 0, x: bump ? -80 : -44, rotate: bump ? -5 : 0 }}
        animate={{ opacity: 1, x: 0, rotate: 0 }}
        transition={
          reduced
            ? { duration: 0.01 }
            : bump
              ? { ...springs.bouncy, delay: 0.32 }
              : { ...springs.gentle, delay: 0.3 }
        }
      />
      <motion.img
        className="rvfx__avatar rvfx__avatar--b"
        src={avatars[1]}
        alt=""
        initial={reduced ? { opacity: 0 } : { opacity: 0, x: bump ? 80 : 44, rotate: bump ? 5 : 0 }}
        animate={{ opacity: 1, x: 0, rotate: 0 }}
        transition={
          reduced
            ? { duration: 0.01 }
            : bump
              ? { ...springs.bouncy, delay: 0.32 }
              : { ...springs.gentle, delay: 0.3 }
        }
      />
      {bump && (
        // a small bubblegum spark where they meet — the "oof"
        <motion.span
          className="rvfx__impact"
          initial={{ opacity: 0, scale: 0.2 }}
          animate={{ opacity: [0, 0.6, 0], scale: [0.2, 2.2, 2.8] }}
          transition={{ duration: 0.45, delay: 0.5, ease: "easeOut" }}
        />
      )}
    </div>
  );
}

export function Reveal({ open, kind, title, kicker, avatars, onClose, children }: RevealProps) {
  const reduced = usePrefersReducedMotion();
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className={`rvfx rvfx--${kind}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.16 } }}
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose?.();
          }}
        >
          {!reduced && kind === "classic" && <ConfettiBurst />}
          <motion.div
            className="rvfx__card"
            role="dialog"
            aria-modal="true"
            aria-label={title}
            initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.86, y: 22 }}
            animate={
              reduced
                ? { opacity: 1 }
                : { opacity: 1, scale: 1, y: 0, transition: { ...springs.dramatic, delay: 0.1 } }
            }
            exit={{ opacity: 0, scale: reduced ? 1 : 0.96, transition: { duration: 0.15 } }}
          >
            {!reduced && kind === "reject" && (
              // one soft bubblegum wash inside the card — a warm pulse, not a strobe
              <motion.div
                className="rvfx__flash"
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 0.4, 0] }}
                transition={{ duration: 0.9, ease: "easeOut" }}
                aria-hidden="true"
              />
            )}
            {/* the mascot's beat: cheer (classic) / shatter-and-wink (reject) */}
            {kind === "classic" ? (
              <div className="rvfx__mascot" aria-hidden="true">
                <motion.span
                  style={{ display: "inline-flex" }}
                  initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.3, y: 10 }}
                  animate={
                    reduced
                      ? { opacity: 1 }
                      : { opacity: 1, scale: 1, y: 0, transition: { ...springs.jelly, delay: 0.25 } }
                  }
                >
                  <Mascot mood="cheer" size={56} />
                </motion.span>
              </div>
            ) : reduced ? (
              <div className="rvfx__mascot" aria-hidden="true">
                <Mascot mood="wink" size={56} />
              </div>
            ) : (
              <TickerShatterBeat />
            )}
            {kicker && <div className="rvfx__kicker">{kicker}</div>}
            {avatars && <AvatarPair avatars={avatars} kind={kind} reduced={reduced} />}
            {kind === "reject" && !reduced ? (
              <motion.div
                className="rvfx__glitchwrap"
                initial={{ opacity: 0, scale: 1.18 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ ...springs.slam, delay: 0.26 }}
              >
                <h2 className="rvfx__title reveal__title">{title}</h2>
              </motion.div>
            ) : (
              <h2 className="rvfx__title reveal__title">{title}</h2>
            )}
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

export interface RevealProps {
  open: boolean;
  kind: "classic" | "reject";
  title: string;
  /** Small caps line above the title. */
  kicker?: string;
  /** Optional pair of avatar URLs — classic eases them together, reject meets with a bounce. */
  avatars?: [string, string];
  onClose?: () => void;
  /** Body copy + action row (use className="rvfx__actions" for the buttons). */
  children?: ReactNode;
}
